import { Box, Button, IconButton, Tooltip, Typography, useTheme } from '@mui/material';
import { tokens } from '../../global/theme/tokens';
import ExoTable from '../../components/exo/ExoTable';
import { useMemo, useState } from 'react';
import {
  ExoTableToolbar,
  ExoTableToolbarItem,
} from '../../components/exo/ExoTableToolbar';
import PlaylistAddIcon from '@mui/icons-material/PlaylistAdd';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ToggleOnIcon from '@mui/icons-material/ToggleOn';
import ToggleOffIcon from '@mui/icons-material/ToggleOff';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import NewWebForm from '../../components/new/NewWebForm';
import Header from '../../components/special/Header';
import { useTranslation } from 'react-i18next';
import { updateOrCreate } from '../../components/special/updateOrCreate';
import useFetch from '../../api/useFetch';
import ExoDialog from '../../components/exo/ExoDialog';
import { useConfig, DBRequest, API_PATCH, API_DELETE } from '../../api/api';
import { LineChart } from '@mui/x-charts';

const WebForms = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);
  const { t } = useTranslation();

  const { apiData, isLoading, setApiData } = useFetch('web-forms');

  const Columns = [
    {
      header: t('Name'),
      key: 'name',
      thAlign: 'left',
      tdAlign: 'left',
      sortable: true,
      width: '100%',
      cell: ({ row }) => (
        <Typography variant="body2" fontWeight={500} sx={{ color: colors.text }}>
          {row.name}
        </Typography>
      ),
    },
    {
      header: t('Lead type'),
      key: 'leadType',
      thAlign: 'right',
      tdAlign: 'right',
      width: '120px',
      hiddenMobile: true,
      cell: ({ row }) => (
        <Typography variant="body2" sx={{ color: colors.text, opacity: 0.6 }}>
          {row.leadType}
        </Typography>
      ),
    },
    {
      header: t('Submissions'),
      key: 'submissionsCount',
      thAlign: 'right',
      tdAlign: 'right',
      width: '120px',
      hiddenMobile: true,
      cell: ({ row }) => (
        <Typography variant="body2" className="monospace" sx={{ color: colors.text, opacity: 0.6 }}>
          {row.submissionsCount ?? 0}
        </Typography>
      ),
    },
    {
      header: t('Status'),
      key: 'isActive',
      thAlign: 'center',
      tdAlign: 'center',
      width: '90px',
      cell: ({ row }) => (
        <Box
          sx={{
            display: 'inline-block',
            width: 8,
            height: 8,
            borderRadius: '50%',
            bgcolor: row.isActive ? colors.success : colors.grey[600],
          }}
        />
      ),
    },
    {
      header: t('Key'),
      key: 'token',
      thAlign: 'center',
      tdAlign: 'center',
      width: '50px',
      hiddenMobile: true,
      cell: ({ row }) => (
        <Tooltip title={t('Copy Form Key')}>
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              navigator.clipboard.writeText(row.token);
            }}
            sx={{ color: colors.text, opacity: 0.4, '&:hover': { opacity: 1, color: colors.primary[400] } }}
          >
            <ContentCopyIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Tooltip>
      ),
    },
  ];

  const [open, setOpen] = useState(false);
  const [editData, setEditData] = useState(null);

  function handleClose() {
    setOpen(false);
    setEditData(null);
  }

  function handleCreate(newWebForm) {
    setApiData(updateOrCreate(apiData, newWebForm));
    handleClose();
  }

  function handleDelete(oldWebForm) {
    setApiData({
      ...apiData,
      data: apiData.data.filter((item) => item.id !== oldWebForm.id),
    });
  }

  return (
    <>
      <Header title={t('Forms')} />
      <ExoTableToolbar>
        <ExoTableToolbarItem
          text={t('New Form')}
          icon={<PlaylistAddIcon />}
          onClick={() => {
            setEditData(null);
            setOpen(true);
          }}
        />
      </ExoTableToolbar>
      <ExoTable
        data={apiData}
        isLoading={isLoading}
        columns={Columns}
        onDelete={handleDelete}
        extendElement={({ row, onUpdate, onDelete }) => (
          <WebFormExtend
            row={row}
            onUpdate={onUpdate}
            onDelete={onDelete}
            onEdit={(data) => {
              setEditData(data);
              setOpen(true);
            }}
          />
        )}
      />
      <ExoDialog open={open} limitWidth>
        <NewWebForm onClose={handleClose} onCreate={handleCreate} startData={editData} />
      </ExoDialog>
    </>
  );
};

// --- Date range helpers (mirrors booking-pages statistics) ---

function formatDate(date) {
  return date.toISOString().split('T')[0];
}

function getDefaultRange(period) {
  const now = new Date();
  const year = now.getFullYear();
  const month = now.getMonth();
  switch (period) {
    case 'daily':
      return { startDate: formatDate(new Date(year, month, 1)), endDate: formatDate(new Date(year, month + 1, 0)) };
    case 'weekly':
      return { startDate: formatDate(new Date(year, month - 2, 1)), endDate: formatDate(new Date(year, month + 1, 0)) };
    case 'monthly':
    case 'quarterly':
      return { startDate: formatDate(new Date(year, 0, 1)), endDate: formatDate(new Date(year, 11, 31)) };
    case 'yearly':
      return { startDate: formatDate(new Date(year - 4, 0, 1)), endDate: formatDate(new Date(year, 11, 31)) };
    default:
      return { startDate: formatDate(new Date(year, 0, 1)), endDate: formatDate(new Date(year, 11, 31)) };
  }
}

function navigateRange(range, period, direction) {
  const start = new Date(range.startDate);
  const end = new Date(range.endDate);
  switch (period) {
    case 'daily': {
      start.setMonth(start.getMonth() + direction);
      return { startDate: formatDate(start), endDate: formatDate(new Date(start.getFullYear(), start.getMonth() + 1, 0)) };
    }
    case 'weekly': {
      start.setMonth(start.getMonth() + direction * 3);
      end.setMonth(end.getMonth() + direction * 3);
      return { startDate: formatDate(start), endDate: formatDate(end) };
    }
    case 'monthly':
    case 'quarterly': {
      start.setFullYear(start.getFullYear() + direction);
      end.setFullYear(end.getFullYear() + direction);
      return { startDate: formatDate(start), endDate: formatDate(end) };
    }
    case 'yearly': {
      start.setFullYear(start.getFullYear() + direction * 5);
      end.setFullYear(end.getFullYear() + direction * 5);
      return { startDate: formatDate(start), endDate: formatDate(end) };
    }
    default:
      return range;
  }
}

function generateFullTimeline(startDate, endDate, period) {
  const labels = [];
  const start = new Date(startDate);
  const end = new Date(endDate);
  if (period === 'daily') {
    const c = new Date(start);
    while (c <= end) { labels.push(formatDate(c)); c.setDate(c.getDate() + 1); }
  } else if (period === 'weekly') {
    const c = new Date(start);
    const dow = c.getDay();
    c.setDate(c.getDate() + (dow === 0 ? -6 : 1 - dow));
    while (c <= end) { labels.push(formatDate(c)); c.setDate(c.getDate() + 7); }
  } else if (period === 'monthly') {
    const c = new Date(start.getFullYear(), start.getMonth(), 1);
    while (c <= end) {
      labels.push(`${c.getFullYear()}-${String(c.getMonth() + 1).padStart(2, '0')}`);
      c.setMonth(c.getMonth() + 1);
    }
  } else if (period === 'quarterly') {
    let y = start.getFullYear();
    let q = Math.ceil((start.getMonth() + 1) / 3);
    const ey = end.getFullYear();
    const eq = Math.ceil((end.getMonth() + 1) / 3);
    while (y < ey || (y === ey && q <= eq)) {
      labels.push(`${y}-Q${q}`);
      q++; if (q > 4) { q = 1; y++; }
    }
  } else if (period === 'yearly') {
    for (let y = start.getFullYear(); y <= end.getFullYear(); y++) labels.push(String(y));
  }
  return labels;
}

function formatTickLabel(label, period) {
  const monthShort = ['Jan', 'Feb', 'Mär', 'Apr', 'Mai', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dez'];
  if (period === 'daily') return String(new Date(label).getDate());
  if (period === 'weekly') { const d = new Date(label); return `${d.getDate()}.${d.getMonth() + 1}.`; }
  if (period === 'monthly') return monthShort[parseInt(label.split('-')[1]) - 1];
  if (period === 'quarterly') return label.split('-')[1];
  return label;
}

function formatRangeLabel(range, period) {
  const start = new Date(range.startDate);
  const end = new Date(range.endDate);
  const m = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  if (period === 'daily') return `${m[start.getMonth()]} ${start.getFullYear()}`;
  if (period === 'weekly') return `${m[start.getMonth()]} ${start.getFullYear()} - ${m[end.getMonth()]} ${end.getFullYear()}`;
  if (period === 'yearly') return `${start.getFullYear()} - ${end.getFullYear()}`;
  return `${start.getFullYear()}`;
}

const PERIODS = [
  { key: 'daily', label: 'Daily' },
  { key: 'weekly', label: 'Weekly' },
  { key: 'monthly', label: 'Monthly' },
  { key: 'quarterly', label: 'Quarterly' },
  { key: 'yearly', label: 'Yearly' },
];

const Kpi = ({ label, value, colors }) => (
  <Box sx={{ px: 2, py: 1.5, borderRadius: 1, bgcolor: colors.card, minWidth: 120 }}>
    <Typography variant="caption" sx={{ color: colors.text, opacity: 0.5, letterSpacing: 0.5 }}>
      {label}
    </Typography>
    <Typography variant="body1" fontWeight={600} sx={{ color: colors.text }}>
      {value}
    </Typography>
  </Box>
);

const WebFormExtend = ({ row, onUpdate, onDelete, onEdit }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);
  const config = useConfig();

  const [period, setPeriod] = useState('monthly');
  const [dateRange, setDateRange] = useState(() => getDefaultRange('monthly'));

  const { apiData: stats } = useFetch(
    `web-forms/${row.id}/statistics?period=${period}&startDate=${dateRange.startDate}&endDate=${dateRange.endDate}`
  );

  const chartData = useMemo(() => {
    const allLabels = generateFullTimeline(dateRange.startDate, dateRange.endDate, period);
    const subMap = {};
    const viewMap = {};
    stats?.data?.submissionTimeline?.forEach((i) => { subMap[i.label] = i.count; });
    stats?.data?.viewTimeline?.forEach((i) => { viewMap[i.label] = i.count; });
    return {
      xLabels: allLabels.map((l) => formatTickLabel(l, period)),
      submissions: allLabels.map((l) => subMap[l] || 0),
      views: allLabels.map((l) => viewMap[l] || 0),
    };
  }, [stats, dateRange, period]);

  const conversion = stats?.data?.conversionRate;

  function handlePeriodChange(p) {
    setPeriod(p);
    setDateRange(getDefaultRange(p));
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, p: 3 }}>
      <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
        <Kpi label={t('Submissions')} value={stats?.data?.totalSubmissions ?? 0} colors={colors} />
        <Kpi label={t('Views')} value={stats?.data?.totalViews ?? 0} colors={colors} />
        <Kpi
          label={t('Conversion')}
          value={conversion != null ? `${(conversion * 100).toFixed(1)}%` : '–'}
          colors={colors}
        />
      </Box>

      {/* Copy: form key + Divi hint */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 2, py: 1.5, borderRadius: 1, bgcolor: colors.card }}>
        <Typography variant="caption" sx={{ color: colors.text, opacity: 0.5, whiteSpace: 'nowrap', mr: 1 }}>
          {t('Form Key')}
        </Typography>
        <Typography variant="body2" className="monospace" noWrap sx={{ flex: 1, color: colors.text, fontSize: '0.8rem' }}>
          {row.token}
        </Typography>
        <IconButton
          size="small"
          onClick={() => navigator.clipboard.writeText(row.token)}
          sx={{ color: colors.text, opacity: 0.4, '&:hover': { opacity: 1, color: colors.primary[400] } }}
        >
          <ContentCopyIcon sx={{ fontSize: 14 }} />
        </IconButton>
      </Box>
      <Typography variant="caption" sx={{ opacity: 0.5 }}>
        {t('Paste this key into the ExoPaper Contact Form module in WordPress/Divi.')}
      </Typography>

      {/* Statistics */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, px: 2, py: 1.5, borderRadius: 1, bgcolor: colors.card }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            {PERIODS.map((p) => (
              <Typography
                key={p.key}
                variant="caption"
                onClick={() => handlePeriodChange(p.key)}
                sx={{
                  cursor: 'pointer',
                  px: 1,
                  py: 0.25,
                  borderRadius: 0.5,
                  fontWeight: period === p.key ? 600 : 400,
                  color: colors.text,
                  opacity: period === p.key ? 1 : 0.5,
                  bgcolor: period === p.key ? colors.selected : 'transparent',
                }}
              >
                {t(p.label)}
              </Typography>
            ))}
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <IconButton size="small" onClick={() => setDateRange((r) => navigateRange(r, period, -1))} sx={{ color: colors.text, opacity: 0.5, p: 0.25 }}>
              <ChevronLeftIcon sx={{ fontSize: 18 }} />
            </IconButton>
            <Typography variant="caption" sx={{ color: colors.text, opacity: 0.5, minWidth: 80, textAlign: 'center' }}>
              {formatRangeLabel(dateRange, period)}
            </Typography>
            <IconButton size="small" onClick={() => setDateRange((r) => navigateRange(r, period, 1))} sx={{ color: colors.text, opacity: 0.5, p: 0.25 }}>
              <ChevronRightIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Box>
        </Box>
        <Box sx={{ width: '100%', height: 200 }}>
          <LineChart
            xAxis={[{ scaleType: 'point', data: chartData.xLabels, tickLabelStyle: { fontSize: 10, fill: colors.text, fillOpacity: 0.4 } }]}
            yAxis={[{ min: 0, tickLabelStyle: { fontSize: 10, fill: colors.text, fillOpacity: 0.4 } }]}
            series={[
              { data: chartData.submissions, label: t('Submissions'), area: true, color: colors.info, showMark: false },
              { data: chartData.views, label: t('Views'), color: colors.success, showMark: false },
            ]}
            height={200}
            grid={{ horizontal: true }}
            sx={{
              '.MuiAreaElement-root': { fillOpacity: 0.06 },
              '.MuiLineElement-root': { strokeWidth: 1.5 },
              '.MuiChartsAxis-line': { stroke: 'transparent' },
              '.MuiChartsAxis-tick': { stroke: 'transparent' },
              '.MuiChartsGrid-line': { stroke: colors.glass, strokeDasharray: '3 3' },
            }}
          />
        </Box>
      </Box>

      {/* Actions */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
        <Button
          size="small"
          startIcon={row.isActive ? <ToggleOnIcon sx={{ fontSize: 18 }} /> : <ToggleOffIcon sx={{ fontSize: 18 }} />}
          onClick={() => {
            DBRequest({
              config,
              path: `web-forms/${row.id}`,
              method: API_PATCH({ isActive: !row.isActive }),
              onResponse: (res) => onUpdate(res.data),
            });
          }}
          sx={{ color: row.isActive ? colors.success : colors.text, opacity: row.isActive ? 1 : 0.5, textTransform: 'none', fontWeight: 400 }}
        >
          {row.isActive ? t('Active') : t('Inactive')}
        </Button>
        <Button
          size="small"
          startIcon={<EditOutlinedIcon sx={{ fontSize: 16 }} />}
          onClick={() => onEdit(row)}
          sx={{ color: colors.text, opacity: 0.5, textTransform: 'none', fontWeight: 400 }}
        >
          {t('Edit')}
        </Button>
        <Button
          size="small"
          startIcon={<DeleteOutlineIcon sx={{ fontSize: 16 }} />}
          onClick={() => {
            DBRequest({
              config,
              path: `web-forms/${row.id}`,
              method: API_DELETE(),
              onResponse: () => onDelete(row),
            });
          }}
          sx={{ color: colors.error, opacity: 0.5, textTransform: 'none', fontWeight: 400 }}
        >
          {t('Delete')}
        </Button>
      </Box>
    </Box>
  );
};

export default WebForms;
